import React, { useEffect, useState } from "react";
import axios from "axios";
import Cropper from "react-easy-crop";
import { Dialog } from "@headlessui/react";
import getCroppedImg from "../utils/cropImage"; 
import {
  FaCog,
  FaUser,
  FaLock,
  FaPalette,
  FaCalendarAlt,
  FaWhatsapp,
  FaHeading,
  FaStop,
  FaAngleDoubleDown,
  FaPhone,
} from "react-icons/fa";
import { toast } from "react-toastify";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Settings() {
  const [darkMode, setDarkMode] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);

  // Existing state...
  const [invoiceDueDate, setInvoiceDueDate] = useState("");
  const [originalInvoiceDueDate, setOriginalInvoiceDueDate] = useState("");
  const [pdfHeader, setPdfHeader] = useState("");
  const [originalPdfHeader, setOriginalPdfHeader] = useState("");
  const [pdfFooter, setPdfFooter] = useState("");
  const [originalPdfFooter, setOriginalPdfFooter] = useState("");
  const [whatsappCallingName, setWhatsappCallingName] = useState("");
  const [originalWhatsappCallingName, setOriginalWhatsappCallingName] =
    useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [originalContactNumber, setOriginalContactNumber] = useState("");
  const [contactName, setContactName] = useState("");
  const [originalContactName, setOriginalContactName] = useState("");
  const [isEditingContactNumber, setIsEditingContactNumber] = useState(false);
  const [isEditingInvoice, setIsEditingInvoice] = useState(false);
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [isEditingFooter, setIsEditingFooter] = useState(false);
  const [isEditingWhatsapp, setIsEditingWhatsapp] = useState(false);
  const [isEditingContactDetails, setIsEditingContactDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invoiceFooterNote, setInvoiceFooterNote] = useState("");
  const [invoiceFooterTerms, setInvoiceFooterTerms] = useState("");
  const [originalInvoiceFooterNote, setOriginalInvoiceFooterNote] =
    useState("");
  const [originalInvoiceFooterTerms, setOriginalInvoiceFooterTerms] =
    useState("");
  const [isEditingInvoiceFooter, setIsEditingInvoiceFooter] = useState(false);

  // Logo States
  const [orgLogo, setOrgLogo] = useState(null); 
  const [originalOrgLogo, setOriginalOrgLogo] = useState(null); // server logo URL
  const [isEditingLogo, setIsEditingLogo] = useState(false);

  // Cropper States
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [tempImage, setTempImage] = useState(null); // raw selected image
  const [orgLogoView, setOrgLogoView] = useState(null); // base64 string for initial view

  const [organizationWallpaper, setOrganizationWallpaper] = useState("");
  const [wallpaperFile, setWallpaperFile] = useState(null);
  const [wallpaperPreview, setWallpaperPreview] = useState("");
  const [mainAdminWallpaper, setMainAdminWallpaper] = useState("");

  const [userRole, setUserRole] = useState(""); // State to store user role

  useEffect(() => {
    const fetchOrganizationSettings = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        const userResponse = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setUserRole(userResponse.data.role);

        const response = await axios.get(
          `${API_BASE_URL}/organizations/getSettings`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setInvoiceDueDate(response.data.invoice_due_date || "");
        setOriginalInvoiceDueDate(response.data.invoice_due_date || "");
        setPdfFooter(response.data.order_cycle_footer || "");
        setOriginalPdfFooter(response.data.order_cycle_footer || "");
        setPdfHeader(response.data.order_cycle_header || "");
        setOriginalPdfHeader(response.data.order_cycle_header || "");
        setWhatsappCallingName(response.data.whatsapp_calling_name || "");
        setOriginalWhatsappCallingName(
          response.data.whatsapp_calling_name || ""
        );
        setContactNumber(response.data.contact_number || "");
        setOriginalContactNumber(response.data.contact_number || "");
        setContactName(response.data.contact_name || "");
        setOriginalContactName(response.data.contact_name || "");
        setInvoiceFooterNote(response.data.invoice_footer_note || "");
        setOriginalInvoiceFooterNote(response.data.invoice_footer_note || "");
        setInvoiceFooterTerms(response.data.invoice_footer_terms || "");
        setOriginalInvoiceFooterTerms(response.data.invoice_footer_terms || "");
        
        const orgWallpaper = response.data.organization_wallpaper || "";
        setOrganizationWallpaper(orgWallpaper);
        setWallpaperPreview(orgWallpaper);

        if (userResponse.data.role === "Main Admin") {
          setMainAdminWallpaper(orgWallpaper);
        }

        if (response.data.logo_url) {
          setOriginalOrgLogo(response.data.logo_url);
        }
        setOrgLogoView(response.data.organization_logo); // base64 string
      } catch (error) {
        toast.error("Error fetching organization settings.");
      }
    };

    fetchOrganizationSettings();
  }, []);

const handleWallpaperChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setWallpaperFile(file);
      setWallpaperPreview(URL.createObjectURL(file));
    }
  };

  const handleWallpaperUpload = async () => {
  if (!wallpaperFile) return;

  const formData = new FormData();
  formData.append("wallpaper", wallpaperFile); // ✅ must match backend

  try {
    setLoading(true);
    const token = localStorage.getItem("token");
    const res = await axios.put(
      `${API_BASE_URL}/organizations/updateWallpaper`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    // Add timestamp query to prevent browser caching
    const newWallpaperUrl = `${res.data.wallpaper}?t=${Date.now()}`;

    setOrganizationWallpaper(newWallpaperUrl);
    setWallpaperPreview(newWallpaperUrl);

    setWallpaperFile(null);
    toast.success("Wallpaper updated successfully");
  } catch (error) {
    toast.error("Failed to upload wallpaper");
    console.error(error);
  } finally {
    setLoading(false);
  }
};



  // Handle file select
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setTempImage(imageUrl);
      setShowCropper(true);
    }
  };

  // Crop complete
  const onCropComplete = (_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  // Save cropped image
  const saveCroppedLogo = async () => {
    try {
      const croppedFile = await getCroppedImg(tempImage, croppedAreaPixels);
      setOrgLogo(croppedFile);
      setShowCropper(false);
      setIsEditingLogo(true);
    } catch (e) {
      toast.error("Failed to crop image");
    }
  };

  const updateOrgLogo = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("logo", orgLogo);

      await axios.put(`${API_BASE_URL}/organizations/updateLogo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Organization logo updated successfully!");
      setOriginalOrgLogo(URL.createObjectURL(orgLogo));
      setIsEditingLogo(false);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update logo.");
    } finally {
      setLoading(false);
    }
  };

  const cancelLogoChange = () => {
    setOrgLogo(null);
    setIsEditingLogo(false);
  };

  // Function to update invoice footer
  const updateInvoiceFooter = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        setLoading(false);
        return;
      }

      await axios.put(
        `${API_BASE_URL}/organizations/updateInvoiceFooter`,
        {
          invoice_footer_note: invoiceFooterNote,
          invoice_footer_terms: invoiceFooterTerms,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Invoice footer updated successfully!");
      setOriginalInvoiceFooterNote(invoiceFooterNote);
      setOriginalInvoiceFooterTerms(invoiceFooterTerms);
      setIsEditingInvoiceFooter(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update invoice footer.");
    } finally {
      setLoading(false);
    }
  };

  // Cancel changes
  const cancelInvoiceFooterChanges = () => {
    setInvoiceFooterNote(originalInvoiceFooterNote);
    setInvoiceFooterTerms(originalInvoiceFooterTerms);
    setIsEditingInvoiceFooter(false);
  };

  // Function to update invoice due date
  const updateInvoiceDueDate = async () => {
    try {
      setLoading(true);

      const dueDateInt = parseInt(invoiceDueDate, 10);
      if (isNaN(dueDateInt) || dueDateInt <= 0 || invoiceDueDate.includes(".")) {
        toast.error("Please enter a valid whole number greater than 0.");
        setLoading(false);
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        setLoading(false);
        return;
      }

      await axios.put(
        `${API_BASE_URL}/organizations/updateDueDate`,
        { invoice_due_date: dueDateInt },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Invoice due date updated successfully!");
      setOriginalInvoiceDueDate(invoiceDueDate); // Update original value
      setIsEditingInvoice(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update invoice due date.");
    } finally {
      setLoading(false);
    }
  };

  // Function to update PDF Header
  const updatePdfHeader = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        setLoading(false);
        return;
      }

      await axios.put(
        `${API_BASE_URL}/organizations/updatePDFHeader`,
        { orderCycle_pdf_header: pdfHeader },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("PDF Header updated successfully!");
      setOriginalPdfHeader(pdfHeader); // Update original value
      setIsEditingHeader(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update PDF Header.");
    } finally {
      setLoading(false);
    }
  };

  // Function to update PDF Footer
  const updatePdfFooter = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        setLoading(false);
        return;
      }

      await axios.put(
        `${API_BASE_URL}/organizations/updatePDFFooter`,
        { orderCycle_pdf_footer: pdfFooter },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("PDF Footer updated successfully!");
      setOriginalPdfFooter(pdfFooter); // Update original value
      setIsEditingFooter(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update PDF Footer.");
    } finally {
      setLoading(false);
    }
  };

  // Function to update PDF Footer
  const updateWhatsappCallingName = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        setLoading(false);
        return;
      }

      await axios.put(
        `${API_BASE_URL}/organizations/updateWhatsappCallingName`,
        { whatsapp_calling_name: whatsappCallingName },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Whatsapp Calling Name updated successfully!");
      setOriginalWhatsappCallingName(whatsappCallingName); // Update original value
      setIsEditingWhatsapp(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update Whatsapp Calling Name.");
    } finally {
      setLoading(false);
    }
  };

  // Function to update Contact Number
  const updateContactDetails = async () => {
    try {

      const trimmedContactNumber = contactNumber.trim();
      const trimmedContactName = contactName.trim();


      if (!trimmedContactNumber.startsWith("+94")) {
        toast.error("Contact Number must start with +94.");
        return;
      }

      const  numberPart = trimmedContactNumber.slice(3); // Remove +94 prefix
      const isValid = /^\d{9}$/.test(numberPart); // Check if the remaining part is 9 digits

      if (!isValid) {
        toast.error("Contact Number must be a 9-digit number after +94.");
        return;
      }

      setLoading(true);

      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        setLoading(false);
        return;
      }

      await axios.put(
        `${API_BASE_URL}/organizations/updateContactNumber`,
        { contact_number: contactNumber,
          contact_name: contactName,
         },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Contact Details updated successfully!");
      setOriginalContactNumber(contactNumber); // Update original value
      setOriginalContactName(contactName);
      setIsEditingContactDetails(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update Contact Number.");
    } finally {
      setLoading(false);
    }
  };

  // Cancel any changes made
  const cancelInvoiceChanges = () => {
    setInvoiceDueDate(originalInvoiceDueDate);
    setIsEditingInvoice(false);
  };

  const cancelPdfHeaderChanges = () => {
    setPdfHeader(originalPdfHeader);
    setIsEditingHeader(false);
  };

  const cancelPdfFooterChanges = () => {
    setPdfFooter(originalPdfFooter);
    setIsEditingFooter(false);
  };

  const cancelWhatsappChanges = () => {
    setWhatsappCallingName(originalWhatsappCallingName);
    setIsEditingWhatsapp(false);
  };

  const cancelContactNumberChanges = () => {
    setContactNumber(originalContactNumber);
    setContactName(originalContactName);
    setIsEditingContactDetails(false);
  };

  return (
    <div className="p-6">
      <h2 className="text-3xl font-bold mb-6 mt-6 flex items-center gap-2">
        <FaCog className="text-gray-700" /> Settings
      </h2>

      {/* Profile Settings */}
      {/* <div className="mb-6 border-b pb-4">
        <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
          <FaUser className="text-blue-500" /> Profile Settings
        </h3>
        <label className="block text-gray-600 mb-2">Username</label>
        <input
          type="text"
          className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
          placeholder="Enter your username"
        />
      </div> */}

      {/* Account Security */}
      {/* <div className="mb-6 border-b pb-4">
        <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
          <FaLock className="text-red-500" /> Account Security
        </h3>
        <label className="block text-gray-600 mb-2">Change Password</label>
        <input
          type="password"
          className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-400"
          placeholder="Enter new password"
        />
      </div> */}

      {/* Preferences */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
          <FaPalette className="text-green-500" /> Preferences
        </h3>
        <div className="flex items-center justify-between">
          <span className="text-gray-700">Dark Mode</span>
          <input
            type="checkbox"
            checked={darkMode}
            onChange={() => setDarkMode(!darkMode)}
            className="toggle-checkbox"
          />
        </div>

        <div className="flex items-center justify-between mt-4">
          <span className="text-gray-700">Email Notifications</span>
          <input
            type="checkbox"
            checked={emailNotifications}
            onChange={() => setEmailNotifications(!emailNotifications)}
            className="toggle-checkbox"
          />
        </div>
      </div>
      {userRole === "Main Admin" && (
        <>
          <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
            <FaPalette /> Organization Wallpaper
          </h3>

          <div className="w-full max-w-xl border-2 border-dashed border-gray-400 rounded-xl p-4 flex flex-col items-center">
            <>
              <input
                type="file"
                accept="image/*"
                onChange={handleWallpaperChange}
                className="mt-3"
              />
              <button
                onClick={handleWallpaperUpload}
                disabled={loading || !wallpaperFile}
                className="mt-3 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? "Uploading..." : "Upload Wallpaper"}
              </button>
            </>
          </div>
        </>
      )}

      {/* Organization Logo */}
      <div className="mb-6 mt-6">
        <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
          <FaStop className="text-yellow-500" /> Organization Logo
        </h3>

        {/* Preview */}
        <div className="mb-3">
          {orgLogo ? (
            <img
              src={URL.createObjectURL(orgLogo)}
              alt="Organization Logo Preview"
              className="h-24 w-24 object-contain border rounded-md"
            />
          ) : orgLogoView ? (
            <img
              src={orgLogoView}
              alt="Organization Logo"
              className="h-24 w-24 object-contain border rounded-md"
            />
          ) : (
            <div className="h-24 w-24 border rounded-md flex items-center justify-center text-gray-400">
              No Logo
            </div>
          )}
        </div>

        <input type="file" accept="image/*" onChange={handleLogoChange} />

        {isEditingLogo && (
          <div className="flex gap-2 mt-4">
            <button
              onClick={updateOrgLogo}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update"}
            </button>
            <button
              onClick={cancelLogoChange}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        )}
      </div>


      {/* Invoice Due Period */}
      <div className="mb-6 mt-6">
        <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
          <FaCalendarAlt className="text-purple-500" /> Invoice Due Period
        </h3>
        <input
          type="number"
          className="p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400 w-1/6"
          value={invoiceDueDate}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d+$/.test(value) || value === "") {
              setInvoiceDueDate(value);
              setIsEditingInvoice(true);
            }
          }}
        />
        <span className="ml-4 font-semibold">Days</span>
        {isEditingInvoice && (
          <div className="flex gap-2 mt-4">
            <button
              onClick={updateInvoiceDueDate}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update"}
            </button>
            <button
              onClick={cancelInvoiceChanges}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Order Cycle PDF Header */}
      <div className="mb-6 mt-6">
        <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
          <FaHeading className="text-blue-500" /> Order Cycle PDF Header
        </h3>
        <textarea
          className="p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400 w-full"
          value={pdfHeader}
          onChange={(e) => {
            setPdfHeader(e.target.value);
            setIsEditingHeader(true);
          }}
        />
        {isEditingHeader && (
          <div className="flex gap-2 mt-4">
            <button
              onClick={updatePdfHeader}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update"}
            </button>
            <button
              onClick={cancelPdfHeaderChanges}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Order Cycle PDF Footer */}
      <div className="mb-6 mt-6">
        <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
          <FaAngleDoubleDown className="text-red-500" /> Order Cycle PDF Footer
        </h3>
        <textarea
          className="p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400 w-full"
          value={pdfFooter}
          onChange={(e) => {
            setPdfFooter(e.target.value);
            setIsEditingFooter(true);
          }}
        />
        {isEditingFooter && (
          <div className="flex gap-2 mt-4">
            <button
              onClick={updatePdfFooter}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update"}
            </button>
            <button
              onClick={cancelPdfFooterChanges}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Invoice Footer */}
      <div className="mb-6 mt-6">
        <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
          <FaStop className="text-red-500" /> Invoice Footer
        </h3>
        
        {/* Note */}
        <label className="block text-gray-700 mb-1">Note</label>
        <textarea
          className="p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400 w-full mb-3"
          value={invoiceFooterNote}
          onChange={(e) => {
            setInvoiceFooterNote(e.target.value);
            setIsEditingInvoiceFooter(true);
          }}
          placeholder="Enter note for invoice footer"
        />
        
        {/* Terms & Conditions */}
        <label className="block text-gray-700 mb-1">Terms & Conditions</label>
        <textarea
          className="p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400 w-full"
          value={invoiceFooterTerms}
          onChange={(e) => {
            setInvoiceFooterTerms(e.target.value);
            setIsEditingInvoiceFooter(true);
          }}
          placeholder="Enter terms & conditions"
        />

        {isEditingInvoiceFooter && (
          <div className="flex gap-2 mt-4">
            <button
              onClick={updateInvoiceFooter}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update"}
            </button>
            <button
              onClick={cancelInvoiceFooterChanges}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        )}
      </div>


      {/* Whatsapp Calling Name */}
      <div className="mb-6 mt-6">
        <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
          <FaWhatsapp className="text-green-500" /> Whatsapp Preferential Name
        </h3>
        <textarea
          className="p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400 w-full"
          value={whatsappCallingName}
          onChange={(e) => {
            setWhatsappCallingName(e.target.value);
            setIsEditingWhatsapp(true);
          }}
        />
        {isEditingWhatsapp && (
          <div className="flex gap-2 mt-4">
            <button
              onClick={updateWhatsappCallingName}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update"}
            </button>
            <button
              onClick={cancelWhatsappChanges}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Contact Details */}
      <div className="mb-6 mt-6 grid grid-cols-2 md:grid-cols-2 gap-4">
        <div className="mb-2 mt-6">
          <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
            <FaPhone className="text-teal-500" /> Organization Contact Number
          </h3>
          <div className="text-gray-600 mb-2 flex">
            <span className="px-3 flex items-center bg-gray-200 border border-r-0 rounded-l-md text-gray-700 select-none">
              +94
            </span>
            <input
              type="tel"
              className="p-2 border rounded-r-md focus:outline-none focus:ring-2 focus:ring-purple-400 w-full"
              value={contactNumber.replace('+94', '')} // display only the 9-digit part
              onChange={(e) => {
                const input = e.target.value.replace(/\D/g, '').slice(0, 9); // digits only, max 9
                setContactNumber(`+94${input}`);
                setIsEditingContactDetails(true);
              }}
              placeholder="Enter 9-digit number"
            />          
          </div>
        </div>
        <div className="mb-2 mt-6">
          <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
            <FaUser className="text-teal-500" /> Contact Name
          </h3>
          <div className="text-gray-600 mb-2 flex">
            <input
              type="text"
              className="p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400 w-full"
              value={contactName}
              onChange={(e) => {
                setContactName(e.target.value);
                setIsEditingContactDetails(true);
              }}
              placeholder="Enter contact name"
            />
          </div>
        </div>
        {isEditingContactDetails && (
            <div className="flex gap-2 mt-2">
              <button
                onClick={updateContactDetails}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? "Updating..." : "Update"}
              </button>
              <button
                onClick={cancelContactNumberChanges}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          )}
      </div>
      {/* Cropper Modal */}
      {showCropper && (
        <Dialog
          open={showCropper}
          onClose={() => setShowCropper(false)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"
        >
          <div className="bg-white rounded-lg p-4 w-[400px] h-[450px] relative">
            <div className="flex justify-between mt-4">
              <button
                onClick={saveCroppedLogo}
                className="px-4 py-2 bg-blue-600 text-white rounded-md"
              >
                Save
              </button>
              <button
                onClick={() => setShowCropper(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md"
              >
                Cancel
              </button>
            </div>

          <div className="w-[300px] h-[300px] relative ml-8 mt-6">

            <Cropper
              image={tempImage}
              crop={crop}
              zoom={zoom}
              aspect={40 / 40}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          </div>
        </Dialog>
      )}

    </div>
  );
}

export default Settings;
